import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Interpolate Email Template Step
 *
 * Generic, reusable step that loads an email template by ID from the
 * database and interpolates {{variable}} placeholders with provided values.
 *
 * Input sockets are generated dynamically from the selected template's
 * variable definitions — each template variable becomes a string input
 * socket (similar to how the switch step generates dynamic output sockets
 * from its configured cases).
 *
 * This step isolates template resolution and rendering so the downstream
 * send_email step stays generic and the upstream data-fetching steps
 * don't need to know about email formatting.
 *
 * Typical workflow: [data step] → interpolate_email_template → send_email
 */
export const interpolateEmailTemplateStep = defineStepConfig({
  type: "interpolate_email_template",
  name: "Interpolate Email Template",
  description:
    "Loads an email template and interpolates {{variable}} placeholders with provided values. Input sockets are generated from the template's variable definitions.",
  category: "Transformation",

  // Dynamic input sockets generated from the selected template's variables.
  // Uses z.record() so that dynamic variable keys survive Zod validation.
  // The real socket definitions come from the template's variables array,
  // generated at instantiation time or in the workflow editor UI.
  inputSchema: z.record(z.string(), z.string()),

  outputSchema: z.object({
    subject: z.string().describe("The interpolated email subject line"),
    htmlContent: z.string().describe("The interpolated HTML email body"),
    textContent: z
      .string()
      .optional()
      .describe(
        "The interpolated plain text email body (if the template defines one)",
      ),
    templateId: z
      .string()
      .uuid()
      .describe("The ID of the resolved email template"),
  }),

  configSchema: z.object({
    templateId: z
      .string()
      .uuid()
      .describe("The ID of the email template to use for interpolation"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "2 seconds",
    },
  },
});
